import React from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { Modal, Header, Container, Grid, Box, SpaceBetween, Button, Alert, StatusIndicator, Badge, ProgressBar, Table, Tabs, Spinner, Pagination, Select } from "@cloudscape-design/components";
import { personalPromotionsStatsModalStore } from "../stores/personalPromotionsStatsModal";

const getLegacyDepositThreshold = (data = {}) => ({
	minDepositAmount: data.minDepositAmount !== undefined ? data.minDepositAmount : 50000,
	maxDepositAmount: data.maxDepositAmount !== undefined ? data.maxDepositAmount : 10000000,
	bonusPercentAmount: data.bonusPercentAmount !== undefined ? data.bonusPercentAmount : 50,
});

const normalizeDepositThresholds = (data = {}) => {
	const thresholds = Array.isArray(data.depositThresholds) ? data.depositThresholds : [getLegacyDepositThreshold(data)];

	return thresholds.map((threshold) => ({
		minDepositAmount: Number(threshold.minDepositAmount) || 0,
		maxDepositAmount: Number(threshold.maxDepositAmount) || 0,
		bonusPercentAmount: Number(threshold.bonusPercentAmount) || 0,
	}));
};

const formatRubles = (kopeks) => ((Number(kopeks) || 0) / 100).toLocaleString();

const formatRublesWithCurrency = (kopeks) => `${formatRubles(kopeks)} ₽`;

const getMaxBonusAmount = (threshold) => {
	const maxDepositAmount = Number(threshold.maxDepositAmount) || 0;
	const bonusPercentAmount = Number(threshold.bonusPercentAmount) || 0;
	return Math.floor((maxDepositAmount * bonusPercentAmount) / 100);
};

class PersonalPromotionsStatsModal extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: false,
			statistics: null,
			error: null,
			activeTab: "overview",
			dailyCurrentPage: 1,
			dailyPageSize: 10,
			usersCurrentPage: 1,
			usersPageSize: 10,
			dailyLoading: false,
			usersLoading: false,
			usersStatusFilter: null,
		};
	}

	isDarkMode = () => {
		return localStorage.getItem("mode") === "dark";
	};

	getCardBackground = () => {
		return this.isDarkMode() ? "#232f3e" : "#f8f9fa";
	};
	componentDidMount() {
		if (this.props.modal.show && this.props.modal.data) {
			this.loadStats(1, 1, true);
		}
	}

	componentDidUpdate(prevProps) {
		if (this.props.modal.show && !prevProps.modal.show && this.props.modal.data) {
			this.setState({
				dailyCurrentPage: 1,
				usersCurrentPage: 1,
			});
			this.loadStats(1, 1, true);
		}
	}

	loadStats = async (dailyPage = 1, usersPage = 1, isInitialLoad = false) => {
		if (isInitialLoad) {
			this.setState({ loading: true, error: null });
		} else {
			if (dailyPage !== this.state.dailyCurrentPage) {
				this.setState({ dailyLoading: true });
			}
			if (usersPage !== this.state.usersCurrentPage) {
				this.setState({ usersLoading: true });
			}
		}

		try {
			const response = await this.props.Interface.request(
				"get",
				{
					class: "data",
					module: "personalPromotions",
					method: "statistics",
				},
				{
					promotionId: this.props.modal.data.id,
					dailyPage: dailyPage || this.state.dailyCurrentPage,
					dailyLimit: this.state.dailyPageSize,
					usersPage: usersPage || this.state.usersCurrentPage,
					usersLimit: this.state.usersPageSize,
					usersStatusFilter: this.state.usersStatusFilter,
				},
			);

			const statistics = {
				overall: response.overall,
				meta: response.meta,
				daily: response.daily.data,
				dailyPagination: {
					totalDays: response.daily.totalDays,
					totalPages: response.daily.totalPages,
					currentPage: response.daily.currentPage,
					pageSize: response.daily.pageSize,
				},
				users: response.users.data,
				usersPagination: {
					totalUsers: response.users.totalUsers,
					totalPages: response.users.totalPages,
					currentPage: response.users.currentPage,
					pageSize: response.users.pageSize,
				},
			};

			this.setState({
				statistics: statistics,
				loading: false,
				dailyLoading: false,
				usersLoading: false,
				dailyCurrentPage: dailyPage || this.state.dailyCurrentPage,
				usersCurrentPage: usersPage || this.state.usersCurrentPage,
			});
		} catch (error) {
			console.error("Error loading statistics:", error);
			this.setState({
				error: error.message || "Failed to load statistics",
				loading: false,
				dailyLoading: false,
				usersLoading: false,
			});
		}
	};

	handleClose = () => {
		this.props.setModal({
			...this.props.modal,
			show: false,
			data: null,
		});
	};

	formatDate = (dateString) => {
		return new Date(dateString).toLocaleDateString("ru-RU");
	};

	hasSplitStatistics = (statistics) => {
		const conditionsCount = Number(statistics?.meta?.conditionsCount) || 0;
		return conditionsCount > 1 && Array.isArray(statistics?.overall?.conditions) && statistics.overall.conditions.length > 0;
	};

	getConditionLabels = (statistics) => {
		const thresholds = Array.isArray(statistics?.meta?.thresholds) ? statistics.meta.thresholds : [];

		if (thresholds.length > 0) {
			return thresholds.map((threshold, index) => `from ${threshold?.minDepositAmount}` || `Condition ${index + 1}`);
		}

		const conditions = Array.isArray(statistics?.overall?.conditions) ? statistics.overall.conditions : [];
		return conditions.map((condition, index) => `Condition ${condition?.index || index + 1}`);
	};

	formatSplitStatistic = (statistics, field, formatter = (value) => value || 0) => {
		const conditions = Array.isArray(statistics?.overall?.conditions) ? statistics.overall.conditions : [];
		return conditions.map((condition) => formatter(condition?.[field])).join(" / ");
	};

	renderStatisticValue = (statistics, field, fallbackValue, formatter = (value) => value || 0) => {
		if (this.hasSplitStatistics(statistics)) {
			return this.formatSplitStatistic(statistics, field, formatter);
		}

		return formatter(fallbackValue);
	};

	renderStatisticLabels = (statistics) => {
		if (!this.hasSplitStatistics(statistics)) {
			return null;
		}

		return (
			<Box variant="small" color="text-body-secondary">
				{this.getConditionLabels(statistics).join(" / ")}
			</Box>
		);
	};

	getStatusIndicator = (status) => {
		switch (status) {
			case "active":
				return <StatusIndicator type="success">Active</StatusIndicator>;
			case "inactive":
				return <StatusIndicator type="warning">Inactive</StatusIndicator>;
			case "used":
				return <StatusIndicator type="info">Used</StatusIndicator>;
			case "expired":
				return <StatusIndicator type="error">Expired</StatusIndicator>;
			default:
				return <StatusIndicator type="info">{status || "Undefined"}</StatusIndicator>;
		}
	};

	getBonusTypeBadge = (item) => {
		const bonusType = item.data?.bonusType || "all";
		let badges = [];

		if (bonusType === "all" || bonusType === "fiat") {
			badges.push(
				<Badge key="fiat" color="blue">
					Fiat
				</Badge>,
			);
		}
		if (bonusType === "all" || bonusType === "crypto") {
			badges.push(
				<Badge key="crypto" color="green">
					Crypto
				</Badge>,
			);
		}

		return badges;
	};

	getPromotionTitle = (item) => {
		if (item.type === "money") {
			const thresholds = normalizeDepositThresholds(item.data);
			return thresholds.length > 1 ? `Money Bonus ${thresholds.length} thresholds` : `Money Bonus ${thresholds[0]?.bonusPercentAmount || 0}%`;
		} else if (item.type === "freespins") {
			return `Freespins Promotion`;
		}
		return `Promotion #${item.id}`;
	};

	render() {
		const { modal } = this.props;
		const item = modal.data;
		const { statistics, loading, error } = this.state;

		if (!item) return null;
		const depositThresholds = normalizeDepositThresholds(item.data);

		return (
			<Modal
				visible={modal.show}
				onDismiss={this.handleClose}
				header={`Statistics: ${this.getPromotionTitle(item)}`}
				size="large"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button onClick={this.handleClose}>Close</Button>
						</SpaceBetween>
					</Box>
				}>
				<SpaceBetween size="l">
					{error && (
						<Alert type="error" header="Error">
							{error}
						</Alert>
					)}

					{loading ? (
						<Container>
							<div style={{ textAlign: "center", padding: "40px" }}>
								<Spinner size="large" />
								<Box margin={{ top: "s" }}>Loading statistics...</Box>
							</div>
						</Container>
					) : (
						<Tabs
							activeTabId={this.state.activeTab}
							onChange={({ detail }) => this.setState({ activeTab: detail.activeTabId })}
							tabs={[
								{
									label: "Overview",
									id: "overview",
									content: (
										<SpaceBetween size="l">
											{/* Basic Information */}
											<Container header={<Header variant="h2">Promotion Details</Header>}>
												<Grid gridDefinition={[{ colspan: { default: 12, xs: 6 } }, { colspan: { default: 12, xs: 6 } }]}>
													<SpaceBetween size="m">
														<div>
															<Box variant="small" color="text-label">
																Type
															</Box>
															<Box variant="h3">
																<Badge color={item.type === "money" ? "blue" : "green"}>{item.type === "money" ? "Money" : "Freespins"}</Badge>
															</Box>
														</div>
														<div>
															<Box variant="small" color="text-label">
																Bonus
															</Box>
															<Box variant="h3">{depositThresholds.length > 1 ? `${depositThresholds.length} thresholds` : `${depositThresholds[0]?.bonusPercentAmount || 0}%`}</Box>
														</div>
														<div>
															<Box variant="small" color="text-label">
																Status
															</Box>
															<div>{this.getStatusIndicator(item.status)}</div>
														</div>
														<div>
															<Box variant="small" color="text-label">
																Bonus Type
															</Box>
															<div style={{ display: "flex", gap: "8px" }}>{this.getBonusTypeBadge(item)}</div>
														</div>
													</SpaceBetween>
													<SpaceBetween size="m">
														<div>
															<Box variant="small" color="text-label">
																Time Limit
															</Box>
															<Box variant="h3">{item.data?.ttlMinutes || 0} minutes</Box>
														</div>
														<div>
															<Box variant="small" color="text-label">
																Deposit Thresholds
															</Box>
															<SpaceBetween size="xxs">
																{depositThresholds.map((threshold, index) => (
																	<Box key={index}>
																		From {formatRubles(threshold.minDepositAmount)} ₽, max bonus {formatRubles(getMaxBonusAmount(threshold))} ₽,
																		{threshold.bonusPercentAmount}%
																	</Box>
																))}
															</SpaceBetween>
														</div>
														<div>
															<Box variant="small" color="text-label">
																Created Date
															</Box>
															<Box>{this.formatDate(item.createdAt)}</Box>
														</div>
														<div>
															<Box variant="small" color="text-label">
																Active Period
															</Box>
															<Box>
																{this.formatDate(item.startDate)} - {this.formatDate(item.endDate)}
															</Box>
														</div>
													</SpaceBetween>
												</Grid>
											</Container>

											<Container header={<Header variant="h2">Live Statistics</Header>}>
												{statistics?.overall ? (
													<SpaceBetween size="l">
														<div
															style={{
																display: "flex",
																flexWrap: "wrap",
																gap: "16px",
															}}>
															<div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", flex: "1 1 220px", minWidth: "220px", textAlign: "center", padding: "20px", background: this.getCardBackground(), borderRadius: "8px" }}>
																<Box variant="h1" color="text-status-info">
																	{statistics.overall.totalActivations || 0}
																</Box>
																<Box variant="p" color="text-label">
																	Total Activations
																</Box>
															</div>
															<div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", flex: "1 1 220px", minWidth: "220px", textAlign: "center", padding: "20px", background: this.getCardBackground(), borderRadius: "8px" }}>
																<Box variant="h1" color="text-status-success">
																	{this.renderStatisticValue(statistics, "usedCount", statistics.overall.usedCount, (value) => value || 0)}
																</Box>
																<Box variant="p" color="text-label">
																	Used
																</Box>
																{this.renderStatisticLabels(statistics)}
															</div>
															<div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", flex: "1 1 220px", minWidth: "220px", textAlign: "center", padding: "20px", background: this.getCardBackground(), borderRadius: "8px" }}>
																<Box variant="h1" color="text-status-warning">
																	{statistics.overall.activeCount || 0}
																</Box>
																<Box variant="p" color="text-label">
																	Active
																</Box>
															</div>
															<div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", flex: "1 1 220px", minWidth: "220px", textAlign: "center", padding: "20px", background: this.getCardBackground(), borderRadius: "8px" }}>
																<Box variant="h1" color="text-status-error">
																	{statistics.overall.expiredCount || 0}
																</Box>
																<Box variant="small" color="text-label">
																	Expired
																</Box>
															</div>
														</div>

														<div
															style={{
																display: "flex",
																flexWrap: "wrap",
																gap: "16px",
															}}>
															<div style={{ flex: "1 1 300px", minWidth: "300px", textAlign: "center", padding: "20px", background: this.getCardBackground(), borderRadius: "8px" }}>
																<Box variant="h2" color="text-status-success">
																	{this.renderStatisticValue(statistics, "avgDepositAmount", statistics.overall.avgDepositAmount, formatRublesWithCurrency)}
																</Box>
																<Box variant="p" color="text-label">
																	Average Deposit
																</Box>
																{this.renderStatisticLabels(statistics)}
															</div>
															<div style={{ flex: "1 1 300px", minWidth: "300px", textAlign: "center", padding: "20px", background: this.getCardBackground(), borderRadius: "8px" }}>
																<Box variant="h2" color="text-status-info">
																	{this.renderStatisticValue(statistics, "totalDepositAmount", statistics.overall.totalDepositAmount, formatRublesWithCurrency)}
																</Box>
																<Box variant="p" color="text-label">
																	Total Deposits
																</Box>
																{this.renderStatisticLabels(statistics)}
															</div>
															<div style={{ flex: "1 1 300px", minWidth: "300px", textAlign: "center", padding: "20px", background: this.getCardBackground(), borderRadius: "8px" }}>
																<Box variant="h2" color="text-status-success">
																	{statistics.overall.totalBonusAmount ? `${(statistics.overall.totalBonusAmount / 100).toLocaleString()} ₽` : "0 ₽"}
																</Box>
																<Box variant="p" color="text-label">
																	Total Bonuses
																</Box>
															</div>
														</div>
													</SpaceBetween>
												) : (
													<Box textAlign="center" padding="l">
														<Box variant="p" color="text-body-secondary">
															No statistics available yet
														</Box>
													</Box>
												)}

												{statistics?.overall && (
													<div style={{ marginTop: "20px" }}>
														<SpaceBetween size="m" direction="vertical">
															<div>
																<Box variant="small" color="text-label">
																	Unique Users
																</Box>
																<Box variant="h3">{statistics.overall.uniqueUsers || 0}</Box>
															</div>
															<div>
																<Box variant="small" color="text-label">
																	Conversion Rate
																</Box>
																<ProgressBar value={statistics.overall.usedCount && statistics.overall.totalActivations ? Math.round((statistics.overall.usedCount / statistics.overall.totalActivations) * 100) : 0} description={`${statistics.overall.usedCount || 0} of ${statistics.overall.totalActivations || 0} activations used`} />
															</div>
														</SpaceBetween>
													</div>
												)}

												{statistics?.meta?.unmatchedUsedRows > 0 && (
													<Alert type="warning" header="Split statistics warning">
														{statistics.meta.unmatchedUsedRows} used rows could not be matched to a deposit condition. Split statistics may be lower than the total Used value.
													</Alert>
												)}
											</Container>
										</SpaceBetween>
									),
								},
								{
									label: "Daily Stats",
									id: "daily",
									content:
										statistics?.daily && statistics.daily.length > 0 ? (
											<div
												style={{
													position: "relative",
													opacity: this.state.dailyLoading ? 0.5 : 1,
													transition: "opacity 0.2s",
												}}>
												{this.state.dailyLoading && (
													<div
														style={{
															position: "absolute",
															top: "50%",
															left: "50%",
															transform: "translate(-50%, -50%)",
															zIndex: 10,
														}}>
														<Spinner size="large" />
													</div>
												)}
												<Table
													header={
														<Header
															variant="h2"
															counter={statistics?.dailyPagination ? `(${statistics.dailyPagination.totalDays})` : ""}
															description={statistics?.dailyPagination && statistics.dailyPagination.totalPages > 1 ? `Showing ${(statistics.dailyPagination.currentPage - 1) * statistics.dailyPagination.pageSize + 1}-${Math.min(statistics.dailyPagination.currentPage * statistics.dailyPagination.pageSize, statistics.dailyPagination.totalDays)} of ${statistics.dailyPagination.totalDays}` : null}
															actions={
																<Select
																	selectedOption={{ label: `${this.state.dailyPageSize} per page`, value: this.state.dailyPageSize.toString() }}
																	onChange={({ detail }) => {
																		const newPageSize = parseInt(detail.selectedOption.value);
																		this.setState({ dailyPageSize: newPageSize, dailyCurrentPage: 1 }, () => {
																			this.loadStats(1, this.state.usersCurrentPage);
																		});
																	}}
																	options={[
																		{ label: "10 per page", value: "10" },
																		{ label: "20 per page", value: "20" },
																		{ label: "50 per page", value: "50" },
																		{ label: "100 per page", value: "100" },
																	]}
																/>
															}>
															Daily Statistics
														</Header>
													}
													items={statistics.daily}
													columnDefinitions={[
														{
															id: "date",
															header: "Date",
															cell: (item) => this.formatDate(item.date),
														},
														{
															id: "activations",
															header: "Activations",
															cell: (item) => item.activations,
														},
														{
															id: "uniqueUsers",
															header: "Unique Users",
															cell: (item) => item.uniqueUsers,
														},
														{
															id: "usedCount",
															header: "Used",
															cell: (item) => item.usedCount,
														},
													]}
													stickyHeader
													empty={
														<Box textAlign="center" padding="l">
															No daily statistics available
														</Box>
													}
													pagination={
														statistics.dailyPagination && statistics.dailyPagination.totalPages > 1 ? (
															<Pagination
																currentPageIndex={statistics.dailyPagination.currentPage}
																onChange={({ detail }) => {
																	this.loadStats(detail.currentPageIndex, this.state.usersCurrentPage);
																}}
																pagesCount={statistics.dailyPagination.totalPages}
																ariaLabels={{
																	nextPageLabel: "Next page",
																	previousPageLabel: "Previous page",
																	pageLabel: (pageNumber) => `Page ${pageNumber}`,
																}}
															/>
														) : null
													}
												/>
											</div>
										) : (
											<Container header={<Header variant="h2">Daily Statistics</Header>}>
												<Box textAlign="center" padding="l">
													<Box variant="p" color="text-body-secondary">
														No daily statistics available yet
													</Box>
												</Box>
											</Container>
										),
								},
								{
									label: "User Details",
									id: "users",
									content:
										statistics?.users && statistics.users.length > 0 ? (
											<div
												style={{
													position: "relative",
													opacity: this.state.usersLoading ? 0.5 : 1,
													transition: "opacity 0.2s",
												}}>
												{this.state.usersLoading && (
													<div
														style={{
															position: "absolute",
															top: "50%",
															left: "50%",
															transform: "translate(-50%, -50%)",
															zIndex: 10,
														}}>
														<Spinner size="large" />
													</div>
												)}
												<Table
													header={
														<Header
															variant="h2"
															counter={statistics?.usersPagination ? `(${statistics.usersPagination.totalUsers})` : ""}
															description={statistics?.usersPagination && statistics.usersPagination.totalPages > 1 ? `Showing ${(statistics.usersPagination.currentPage - 1) * statistics.usersPagination.pageSize + 1}-${Math.min(statistics.usersPagination.currentPage * statistics.usersPagination.pageSize, statistics.usersPagination.totalUsers)} of ${statistics.usersPagination.totalUsers}` : null}
															actions={
																<SpaceBetween direction="horizontal" size="xs">
																	<Select
																		selectedOption={this.state.usersStatusFilter ? { label: this.state.usersStatusFilter.charAt(0).toUpperCase() + this.state.usersStatusFilter.slice(1), value: this.state.usersStatusFilter } : { label: "All statuses", value: null }}
																		onChange={({ detail }) => {
																			const statusFilter = detail.selectedOption.value === null ? null : detail.selectedOption.value;
																			this.setState({ usersStatusFilter: statusFilter, usersCurrentPage: 1 }, () => {
																				this.loadStats(this.state.dailyCurrentPage, 1);
																			});
																		}}
																		options={[
																			{ label: "All statuses", value: null },
																			{ label: "Active", value: "active" },
																			{ label: "Used", value: "used" },
																			{ label: "Expired", value: "expired" },
																		]}
																		placeholder="Filter by status"
																	/>
																	<Select
																		selectedOption={{ label: `${this.state.usersPageSize} per page`, value: this.state.usersPageSize.toString() }}
																		onChange={({ detail }) => {
																			const newPageSize = parseInt(detail.selectedOption.value);
																			this.setState({ usersPageSize: newPageSize, usersCurrentPage: 1 }, () => {
																				this.loadStats(this.state.dailyCurrentPage, 1);
																			});
																		}}
																		options={[
																			{ label: "10 per page", value: "10" },
																			{ label: "20 per page", value: "20" },
																			{ label: "50 per page", value: "50" },
																			{ label: "100 per page", value: "100" },
																		]}
																	/>
																</SpaceBetween>
															}>
															User Activations
														</Header>
													}
													items={statistics.users}
													columnDefinitions={[
														{
															id: "uid",
															header: "User ID",
															cell: (item) => item.uid,
														},
														{
															id: "status",
															header: "Status",
															cell: (item) => this.getStatusIndicator(item.status),
														},
														{
															id: "createdAt",
															header: "Activated",
															cell: (item) => this.formatDate(item.createdAt),
														},
														{
															id: "usedAt",
															header: "Used",
															cell: (item) => (item.usedAt ? this.formatDate(item.usedAt) : "-"),
														},
														{
															id: "bonus",
															header: "Bonus %",
															cell: (item) => {
																const data = typeof item.data === "string" ? JSON.parse(item.data) : item.data;
																const thresholds = normalizeDepositThresholds(data);
																return thresholds.map((threshold) => `${threshold.bonusPercentAmount}%`).join(", ");
															},
														},
														{
															id: "depositAmount",
															header: "Deposit",
															cell: (item) => (item.depositAmount ? `${(item.depositAmount / 100).toLocaleString()} ₽` : "-"),
														},
														{
															id: "bonusAmount",
															header: "Bonus Amount",
															cell: (item) => (item.bonusAmount ? `${(item.bonusAmount / 100).toLocaleString()} ₽` : "-"),
														},
													]}
													stickyHeader
													empty={
														<Box textAlign="center" padding="l">
															No user activations yet
														</Box>
													}
													pagination={
														statistics.usersPagination && statistics.usersPagination.totalPages > 1 ? (
															<Pagination
																currentPageIndex={statistics.usersPagination.currentPage}
																onChange={({ detail }) => {
																	this.loadStats(this.state.dailyCurrentPage, detail.currentPageIndex);
																}}
																pagesCount={statistics.usersPagination.totalPages}
																ariaLabels={{
																	nextPageLabel: "Next page",
																	previousPageLabel: "Previous page",
																	pageLabel: (pageNumber) => `Page ${pageNumber}`,
																}}
															/>
														) : null
													}
												/>
											</div>
										) : (
											<Container header={<Header variant="h2">User Activations</Header>}>
												<Box textAlign="center" padding="l">
													<Box variant="p" color="text-body-secondary">
														No user activations yet
													</Box>
												</Box>
											</Container>
										),
								},
							]}
						/>
					)}
				</SpaceBetween>
			</Modal>
		);
	}
}

function PersonalPromotionsStatsModalWithRecoil(props) {
	const modal = useRecoilValue(personalPromotionsStatsModalStore);
	const setModal = useSetRecoilState(personalPromotionsStatsModalStore);
	return <PersonalPromotionsStatsModal {...props} modal={modal} setModal={setModal} Interface={props.Interface} />;
}

export default PersonalPromotionsStatsModalWithRecoil;
